import React, { useEffect, useState } from "react";
import { useEffectOnce } from "../../hooks/useeffectonce";
import { useDispatch, useSelector } from "react-redux";
import { addNotification } from "../../redux/slices/notificationsslice";
import { isUserResetPasswordValid, saveEntity, setProcessing, setFormServerErrors, disposePage } from "../../redux/slices/resetpasswordpageslice";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { api, apiErrorsHandler, getErrorMessages } from "../../api/httpclient";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormMultiSelect } from "../../forms/form-components";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { isEmptyObject, notificationDurationMiddle, notificationDurationLong, transformReportsMenu, isValidEmail } from "../../lib/utils";
import { APP_HEADER_HEIGHT } from "../../settings";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const ResetPasswordPage = props => {
    const dispatch = useDispatch();
    const pageState = useSelector(state => state.resetPasswordPage);
    const currentUser = useSelector(state => state.currentUser);
    const currentTheme = useSelector(state => state.currentTheme);

    const { t }  = useTranslation();

    const navigate = useNavigate();
    const location = useLocation();
    
    const formValidator = values =>{
        let result = {};

        let newPassword = values.newPassword;
        let newPasswordConfirmed = values.newPasswordConfirmed;

        if (!newPassword)
            result.newPassword = t("resetpass.specify");
        
        if (!newPasswordConfirmed)
            result.newPasswordConfirmed = t("resetpass.specify1");

        if (newPassword && newPasswordConfirmed && newPassword != newPasswordConfirmed)
            result.newPasswordConfirmed = t("resetpass.specify2");

        return isEmptyObject(result) ? undefined : result;
    };

    const {id} = useParams();

    const [code, setCode] = useState(id);

    useEffectOnce(() =>{
        //an auth user shall not see this page and must be redirected to /
        if (currentUser.IsAuthenticated)
        {
            navigate("/", { replace: true});
            return;
        } 
        
        let _id = id;

        if (!_id || _id.toString().trim() == "")
        {
            //invalid code
            navigate("/", {replace: true});
            return;
        }

        dispatch(isUserResetPasswordValid(_id.toString().trim(), t))
        .then(response => {
            dispatch(setProcessing({processing: false}));

            let data = response.data.Data;            

            if (data.Expired)
            {
                dispatch(addNotification({text: t("resetpass.generate"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                navigate("/login", {replace: true});                          
                return;
            }
            else if (!data.IsValid)
            {
                navigate("/", {replace: true});                          
                return;
            }

            setCode(_id.toString().trim());
        },
        err => {
            dispatch(setProcessing({processing: false}));

            navigate("/", {replace: true});                          
            return;
        });    
        

        return () => {
            dispatch(disposePage());
        }
    }, []);


   const handleSave = formRendererProps => {
        let newPassword =  formRendererProps.valueGetter("newPassword");
        let newPasswordConfirmed = formRendererProps.valueGetter("newPasswordConfirmed");        

        if (formValidator({newPassword, newPasswordConfirmed}) !== undefined)
            return;
            
        dispatch(saveEntity(newPassword, newPasswordConfirmed, code, t))
        .then(
            response => 
            {
                dispatch(setProcessing({processing: false}));

                dispatch(addNotification({text: t("resetpass.success") , type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

                navigate("/login", {replace: true});                          
            },
            err => {
                dispatch(setProcessing({processing: false}));

                const handleResponseReceived = (state, dispatch, navigate, payload) => {
                    let errorMessages = payload?.data?.ErrorMessages;

                    if (!errorMessages || errorMessages.length == 0)
                        errorMessages = [t("resetpass.unknown_error")];
                    else     
                    {               
                        errorMessages = getErrorMessages(errorMessages, 1);     
                        
                        if (payload.data.ErrorCodes.CodeExpired)
                        {
                            dispatch(addNotification({text: t("resetpass.expired"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                            navigate("/login", {replace: true});  
                            return;
                        }
                        else if (payload.data.ErrorCodes.PasswordTooShort || payload.data.ErrorCodes.PasswordRequiresLower  || payload.data.ErrorCodes.PasswordRequiresUpper || payload.data.ErrorCodes.PasswordRequiresDigit  || payload.data.ErrorCodes.PasswordRequiresSpecial)
                        {
                            dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration: notificationDurationLong, position:"topRight"}));
                            return;
                        }
                    }

                    dispatch(addNotification({text: t("resetpass.unknown_error"), type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                };

                const handleUnhandled = (state, dispatch, navigate, payload) =>{
                    let errorMessage = t("resetpass.unknown_error");
                    dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration: notificationDurationMiddle, position:"topRight"}));
                };

                apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});                
            }
        );
   };
       

   return (
    <div
        className="resetpassword-page"           
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{opacity:0}}
        transition={{ duration:1 }}
       >
        <div className="layer-outer">
            <div className="layer-inner">
            </div>
        </div>
        {
            pageState.processing && 
            <div className="page-loader">
                <Loader size={"large"} type="infinite-spinner" themeColor={"primary"} />
                {
                    pageState.processingText && 
                    <div style={{fontSize: 13, color: currentTheme.primary}}>{pageState.processingText}</div>
                }
            </div>
        }
        {
            !pageState.processing && 
            <div style={{}} className={"resetpassword-form-wrapper"}>                              
                <div style={{display: "flex", justifyContent: "center", alignItems: "center", flex: 1}}>
                    <Form    
                        validator={formValidator} 
                        initialValues={{
                            newPassword: "",
                            newPasswordConfirmed: ""
                        }}
                        render={(formRenderProps) => (
                            <FormElement horizontal={false}
                                style={{
                                    // width: 250,
                                }}
                            >                              
                                <Field
                                    id={"newPassword"}
                                    name={"newPassword"}
                                    label={t("resetpass.newpass")}
                                    type="password"
                                    component={FormInput}       
                                    autoFocus={true}
                                />

                                <Field
                                    id={"newPasswordConfirmed"}
                                    name={"newPasswordConfirmed"}
                                    label={t("resetpass.newpassconfirm")}
                                    type="password"
                                    component={FormInput}
                                    style={{}}
                                />
                            
                                <div className="k-form-buttons" style={{justifyContent: "space-between"}}>
                                    <Button
                                        style={{padding: "6px 28px"}}
                                        themeColor={"primary"}
                                        onClick={e => {
                                            handleSave(formRenderProps);
                                        }}
                                        disabled={pageState.processing}
                                        >
                                        {t("resetpass.submit")}
                                        </Button>
                                        <Button 
                                            style={{padding: "6px 28px"}}
                                            onClick={ (e) => {
                                            formRenderProps.onFormReset();                                
                                        }}
                                        disabled={pageState.processing}>{t("resetpass.clear")}</Button>
                                </div>
                            </FormElement>)}
                    />
                </div>                
            </div>
        }
        <style>
            {`
                .resetpassword-page
                {
                    display: flex;
                    height: 100%;
                    align-items: center;
                    flex: 1;
                    justify-content: center;
                    position: relative;
                    background-color: ${currentTheme.appBarHomeBg};                    
                }

                .resetpassword-pager .page-loader
                {
                    position: absolute;
                    z-index: 10005;
                    top: calc(50% - ${APP_HEADER_HEIGHT}px); 
                    left: calc(50% - 120px);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    padding: 20px;
                    background-color: ${currentTheme.componentBg};
                    border: 1px solid ${currentTheme.componentBorderColor};
                    border-radius: 6px;
                }

                .resetpassword-page .layer-outer
                {
                    position: absolute;
                    z-index: 3;    
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: linear-gradient(to top, #0d42b2 0%, #0d42b2 5%, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0) 100%);
                }

                .resetpassword-page .layer-inner
                {
                    background-image: linear-gradient(15deg, #5286D1 0%, #436EAB 5%, transparent 50%, transparent 100%) !important;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                }


                .resetpassword-page .resetpassword-form-wrapper
                {
                    z-index: 99;
                    width: 500px;
                    border: 1px solid #E6E6E6;
                    border-radius: 8px;
                    padding: 0;
                    display: flex;
                    flex-direction: column;;
                    align-items: stretch;
                    min-height: 350px;
                    background-color: ${currentTheme.componentBg};
                    margin-bottom: 42px;
                }

                .resetpassword-page .resetpassword-form-wrapper .k-form-field:nth-child(1)
                {
                    /*width: 350px;*/
                    margin-top: 0px;
                }
                .resetpassword-page .resetpassword-form-wrapper .k-form-field:nth-child(2)
                {
                    /*width: 350px;*/
                    margin-top: 32px !important;
                }

                .resetpassword-page .resetpassword-form-wrapper .selections
                {
                    padding: 8px;
                    border: 1px solid #E6E6E6;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    display: flex;
                    flex-direction: row-reverse;
                    justify-content: space-between;
                    align-items: center;
                   /*background-color: #4E9FFF;*/
                   background-image: linear-gradient(to right, #F5F5F5, #E6E6E6);
                }

              
                .resetpassword-page .k-form .k-input
                {
                    padding: 8px;
                    margin-top: 4px;
                    width: 400px;
                }
            `}
        </style>
    </div>
   );
};

//export const LoginPage = {Component: Login};

